import React, { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api_url } from '../../constant/constant';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const Trainingcampaignsprogress = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignInfo, setCampaignInfo] = useState({});
  const [activityStats, setActivityStats] = useState({});
  const [quizPerLesson, setQuizPerLesson] = useState([]);
  const [videoWatchedPerLesson, setVideoWatchedPerLesson] = useState([]);
  const [quizPassedPerLesson, setQuizPassedPerLesson] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState([]);
  const colors = ['#34d399', '#60a5fa', '#f87171', '#fbbf24', '#a78bfa', '#4ade80'];

  useEffect(() => {
    fetchDepartments();
    fetchCampaigns();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/admin/main/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data) setDepartments(result.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/learning-campaigns`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (result.learning_campaigns?.data) setCampaigns(result.learning_campaigns.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${api_url}/report/learning/campaign-progress?campaign_id=${selectedCampaign}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if ([401, 403, 429].includes(response.status)) {
        localStorage.removeItem('userToken');
        navigate('/login');
        return;
      }

      const result = await response.json();

      if (result.status === 'success') {
        setCampaignInfo(result.campaign_statistics || {});
        setActivityStats(result.campaign_activity_statistics || {});

        setQuizPerLesson(
          result.lesson_statistics?.quiz_per_lesson?.map(item => ({
            title: item.lesson_title,
            count: item.quiz_solved,
          })) || []
        );

        setVideoWatchedPerLesson(
          result.lesson_statistics?.watch_per_lesson?.map(item => ({
            title: item.lesson_title,
            count: item.watch_count,
          })) || []
        );

        setQuizPassedPerLesson(
          result.lesson_statistics?.quiz_success_per_lesson?.map(item => ({
            title: item.lesson_title,
            count: item.quiz_passed,
          })) || []
        );

        setActiveUsers(result.active_users?.data || []);
        setInactiveUsers(result.inactive_users?.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch campaign data.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className='container p-3'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='pageTitle'>{t('TrainingCampaignDashboard')}</div>
          <Button variant='primary' onClick={() => navigate(-1)}>
            {t('Back')}
          </Button>
        </div>
      </div>

      <div className='mb-4 p-3 card'>
        <div className='p-3' style={{ backgroundColor: '#f0f4f8', borderRadius: '16px' }}>
          <div className='row g-3 align-items-center'>
            <div className='col-md-4'>
              <label className='form-label'>{t('SelectCampaign')}</label>
              <select
                className='form-select'
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
              >
                <option value=''>{t('SelectCampaign')}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.title_en}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-4'>
              <label className='form-label'>{t('SelectDepartments')}</label>
              <select
                className='form-select'
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value=''>{t('SelectDepartment')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Button
                style={{ minWidth: '100px' }}
                className='btn btn-success'
                onClick={fetchData}
                disabled={!selectedCampaign}
              >
                {t('ApplyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && <div className='text-center py-5'>{t('Loading')}...</div>}
      {error && <div className='text-danger text-center py-5'>{error}</div>}

      {/* Campaign Info Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <Card className="p-3 text-center">
            <h4 className="fw-bold">{campaignInfo?.campaign_title || '-'}</h4>
            <small className="text-muted">{t('CampaignTitle')}</small>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="p-3 text-center">
            <h4 className="fw-bold">{campaignInfo?.total_users || 0}</h4>
            <small className="text-muted">{t('NumberOfEnrolledUsers')}</small>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="p-3 text-center">
            <h4 className="fw-bold">{campaignInfo?.completed_users || 0}</h4>
            <small className="text-muted">{t('CompletedUsers')}</small>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="p-3 text-center">
            <h4 className="fw-bold">{campaignInfo?.total_lessons || 0}</h4>
            <small className="text-muted">{t('NumberOfLessons')}</small>
          </Card>
        </div>
      </div>

      {/* Campaign Activity Summary */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <Card className="p-3 text-center">
            <h4 className="fw-bold">{activityStats?.watch_percentage || 0}%</h4>
            <small className="text-info">{t('WatchPercentage')}</small>
          </Card>
        </div>
        <div className="col-md-6">
          <Card className="p-3 text-center">
            <h4 className="fw-bold">{activityStats?.participation_percentage || 0}%</h4>
            <small className="text-primary">{t('ParticipationPercentage')}</small>
          </Card>
        </div>
      </div>

      {/* Bar Charts Section - Each chart in own row, bigger size */}
      <div className="mb-4">
        {/* Quiz Taken */}
        <Card className="shadow-sm p-4 mb-4" style={{ direction: t('dir') === 'rtl' ? 'rtl' : 'ltr' }}>
          <h6 className="mb-4" style={{ textAlign: t('dir') === 'rtl' ? 'right' : 'left' }}>
            {t('QuizTakenPerLesson')}
          </h6>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={quizPerLesson}>
              <XAxis dataKey="title" angle={-15} textAnchor={t('dir') === 'rtl' ? 'start' : 'end'} height={50} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#60a5fa" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Video Watched */}
        <Card className="shadow-sm p-4 mb-4" style={{ direction: t('dir') === 'rtl' ? 'rtl' : 'ltr' }}>
          <h6 className="mb-4" style={{ textAlign: t('dir') === 'rtl' ? 'right' : 'left' }}>
            {t('VideoWatchedPerLesson')}
          </h6>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={videoWatchedPerLesson}>
              <XAxis dataKey="title" angle={-15} textAnchor={t('dir') === 'rtl' ? 'start' : 'end'} height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count">
                {videoWatchedPerLesson.map((entry, index) => (
                  <Cell key={index} fill={colors[index % colors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Quiz Passed */}
        <Card className="shadow-sm p-4" style={{ direction: t('dir') === 'rtl' ? 'rtl' : 'ltr' }}>
          <h6 className="mb-4" style={{ textAlign: t('dir') === 'rtl' ? 'right' : 'left' }}>
            {t('QuizPassedPerLesson')}
          </h6>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={quizPassedPerLesson}>
              <XAxis dataKey="title" angle={-15} textAnchor={t('dir') === 'rtl' ? 'start' : 'end'} height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#34d399" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
};

export default Trainingcampaignsprogress;
